import { useAskInputActions } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { getBootstrapConfig } from '#pplx/frontend/apps/ask/bootstrap-config/bootstrapConfig.js';
import { useCometState, useEntropyBrowser, } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { useShortcutsTypeaheadQuery } from '#pplx/frontend/apps/ask/hooks/tasks-hooks/useShortcutsTypeaheadQuery.js';
import { useIsIncognito } from '#pplx/frontend/apps/ask/incognito/useIsIncognito.js';
import { fetchBlankStateSuggestions } from '#pplx/frontend/apps/ask/queries/homepage-queries/fetchBlankStateSuggestions.js';
import { getFinanceSuggestions } from '#pplx/frontend/apps/ask/suggest-dropdown/fetchers/getFinanceSuggestions.js';
import { getHealthSuggestions } from '#pplx/frontend/apps/ask/suggest-dropdown/fetchers/getHealthSuggestions.js';
import { getSuggestionsLimit } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/utils/utils.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { getSuggestsFromQuickActions } from '#pplx/frontend/comet/client/utils.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { domainFromURL } from '#pplx/frontend/common/files/urls.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { isComputerQuerySource } from '#pplx/frontend/stream/types.js';
import { keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
const useBlankStateSuggestSearch = ({ sources, attachments, searchMode, focus, isCometHome, reason, enabled, }) => {
    const { sidecarSourceTab: { url: sourceTabUrl }, } = useCometState();
    const bootstrapConfig = getBootstrapConfig();
    const entropyRenderingPlace = bootstrapConfig.erp;
    const { isMobileUserAgent } = useIsMobile();
    const cometBrowser = useEntropyBrowser();
    const { isIncognito } = useIsIncognito({ reason });
    const { data: shortcuts } = useShortcutsTypeaheadQuery({ reason });
    const queryKey = useMemo(() => {
        // The query key assumes we reload if contextual params like `locale` (from cookie) changes.
        if (isCometHome && searchMode === SearchMode.SEARCH) {
            // Though it's not reflected in this file, search mode does have an
            // affect and we do show different suggestions for different modes.
            return QueryClientHelper.makeEphemeralQueryKey('comet-blank-state-suggest', isIncognito, searchMode, true, shortcuts);
        }
        if (focus === 'finance') {
            return QueryClientHelper.makeEphemeralQueryKey('finance-blank-state-suggest', isIncognito);
        }
        if (focus === 'health') {
            return QueryClientHelper.makeEphemeralQueryKey('health-blank-state-suggest', isIncognito);
        }
        return QueryClientHelper.makeEphemeralQueryKey('blank-state-suggest', isIncognito, sources, attachments, searchMode, entropyRenderingPlace === 'sidecar' ? sourceTabUrl : '');
    }, [
        sources,
        attachments,
        searchMode,
        focus,
        isCometHome,
        isIncognito,
        sourceTabUrl,
        entropyRenderingPlace,
        shortcuts,
    ]);
    return useQuery({
        queryKey,
        enabled,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            if (focus === 'finance') {
                const suggestions = await getFinanceSuggestions({
                    query: '',
                    reason,
                    // TODO(@john-michael-murphy): Consider supporting other countries here
                    // like we do on finance pages.
                    country: 'US',
                });
                const options = suggestions.map((suggestion, index) => ({
                    query: suggestion.query || `suggestion-${index}`,
                    title: suggestion.title ?? undefined,
                    image: suggestion.image ?? undefined,
                    description: suggestion.description ?? undefined,
                    category: suggestion.category ?? undefined,
                    query_uuid: suggestion.query_uuid ?? undefined,
                    url: suggestion.url ?? undefined,
                    metadata: suggestion.metadata ?? undefined,
                }));
                return options;
            }
            if (focus === 'health') {
                const suggestions = await getHealthSuggestions({ reason });
                return suggestions.slice(0, getSuggestionsLimit(isMobileUserAgent));
            }
            // Original behavior for non-finance focus
            const [blankStateResults, quickActionsSuggests] = await Promise.all([
                fetchBlankStateSuggestions({
                    // Note: The BE uses the length of the sources array to determine writing vs internet
                    // Remove after we refactor the BE to remove search focus
                    sources,
                    attachments,
                    searchMode,
                    sourceTabUrl: entropyRenderingPlace === 'sidecar' ? sourceTabUrl : '',
                    entropyRenderingPlace,
                    reason,
                }),
                entropyRenderingPlace === 'sidecar'
                    ? getSuggestsFromQuickActions({
                        entropyBrowser: cometBrowser,
                        url: sourceTabUrl,
                    })
                    : Promise.resolve([]),
            ]);
            return [...quickActionsSuggests, ...blankStateResults].slice(0, getSuggestionsLimit(isMobileUserAgent));
        },
    });
};
/** Loads and restores blank-state rows using the input's source and mode policy. */
export const useBlankStateSuggest = (props) => {
    const { querySource } = props;
    // Computer inputs do not offer personalized blank-state suggestions.
    const suggestionsDisabled = props.suggestionsDisabled || isComputerQuerySource(querySource);
    const { setSuggestions, setBlankStateSuggestions } = useAskInputActions();
    const { sidecarSourceTab: { url: sourceTabUrl, secondaryTab }, } = useCometState();
    const bootstrapConfig = getBootstrapConfig();
    const entropyRenderingPlace = bootstrapConfig.erp;
    const prevDomainRef = useRef(undefined);
    const firstFetchSearchModeRef = useRef(props.selectedSearchMode);
    const hasSetSuggestionsRef = useRef(false);
    const currentDomain = sourceTabUrl ? domainFromURL(sourceTabUrl) : undefined;
    const skipBlankStateSuggestions = useMemo(() => {
        if (suggestionsDisabled) {
            return true;
        }
        const isSidecar = entropyRenderingPlace === 'sidecar';
        const isSidecarWithSameDomain = isSidecar && currentDomain && currentDomain === prevDomainRef.current;
        const isSidecarWithSplit = isSidecar && secondaryTab;
        return isSidecarWithSameDomain || isSidecarWithSplit;
    }, [suggestionsDisabled, entropyRenderingPlace, currentDomain, secondaryTab]);
    if (!skipBlankStateSuggestions && prevDomainRef.current !== currentDomain) {
        prevDomainRef.current = currentDomain;
    }
    const { data: suggestions } = useBlankStateSuggestSearch({
        ...props,
        focus: querySource === 'finance' ? 'finance' : querySource === 'health' ? 'health' : undefined,
        searchMode: props.selectedSearchMode,
        enabled: !skipBlankStateSuggestions,
    });
    useEffect(() => {
        if (skipBlankStateSuggestions)
            setBlankStateSuggestions(EMPTY_ARRAY, props.selectedSearchMode);
        else if (suggestions)
            setBlankStateSuggestions(suggestions, props.selectedSearchMode);
    }, [props.selectedSearchMode, setBlankStateSuggestions, skipBlankStateSuggestions, suggestions]);
    useEffect(() => {
        // set blank state suggestions to blank state results if either:
        // - not-first fetch (for first, only render upon click or query change)
        // - entropy sidecar (automatically open sidecar suggestions on load)
        if ((props.query?.length ?? 0) > 0)
            return;
        if (!suggestions)
            return;
        if (entropyRenderingPlace === 'sidecar' ||
            hasSetSuggestionsRef.current ||
            firstFetchSearchModeRef.current !== props.selectedSearchMode) {
            setSuggestions(suggestions);
            hasSetSuggestionsRef.current = true;
        }
    }, [props.query, props.selectedSearchMode, suggestions, setSuggestions, entropyRenderingPlace]);
    return null;
};
//# sourceMappingURL=useBlankStateSuggest.js.map